import React from 'react';
import {
  TextField,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  OutlinedInput,
  InputLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const ReusableField = ({
  label,
  value,
  type = 'text',
  onChange,
  name,
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  placeholder = '',
  options = [],
  multiline = false,
  rows = 4,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  sx = {},
  inputProps = {},
  ...otherProps
}) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  // Check if this field is using react-hook-form register
  const isUsingRegister = otherProps.ref !== undefined;

  const renderField = () => {
    switch (type) {
      case 'text':
      case 'email':
      case 'number':
      case 'tel':
      case 'url':
        return (
          <TextField
            label={label}
            value={isUsingRegister ? undefined : (value || '')}
            onChange={isUsingRegister ? undefined : onChange}
            name={name}
            type={type}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            multiline={multiline}
            rows={rows}
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            sx={sx}
            inputProps={inputProps}
            {...otherProps}
          />
        );

      case 'password':
        return (
          <TextField
            label={label}
            value={isUsingRegister ? undefined : (value || '')}
            onChange={isUsingRegister ? undefined : onChange}
            name={name}
            type={showPassword ? 'text' : 'password'}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            sx={sx}
            inputProps={inputProps}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handlePasswordToggle}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...otherProps}
          />
        );

      case 'select': {
        const normalizedOptions = options.map((opt) => ({
          value: opt.value !== undefined ? opt.value : (opt.id !== undefined ? opt.id : opt),
          label: opt.label !== undefined ? opt.label : (opt.title !== undefined ? opt.title : String(opt)),
        }));
        const effectiveValue = isUsingRegister
          ? (value !== undefined ? value : undefined)
          : (value || '');
        const effectiveOnChange = isUsingRegister
          ? (onChange ? onChange : undefined)
          : onChange;
        return (
          <FormControl
            fullWidth={fullWidth}
            size={size}
            error={error}
            disabled={disabled}
            required={required}
            sx={sx}
          >
            <InputLabel>{label}</InputLabel>
            <Select
              value={effectiveValue}
              onChange={effectiveOnChange}
              name={name}
              label={label}
              input={<OutlinedInput label={label} />}
              {...otherProps}
            >
              {normalizedOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        );
      }

      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={isUsingRegister ? undefined : (value || false)}
                onChange={isUsingRegister ? undefined : onChange}
                name={name}
                disabled={disabled}
                {...otherProps}
              />
            }
            label={label}
            sx={sx}
          />
        );

      case 'switch':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={isUsingRegister ? undefined : (value || false)}
                onChange={isUsingRegister ? undefined : onChange}
                name={name}
                disabled={disabled}
                {...otherProps}
              />
            }
            label={label}
            sx={sx}
          />
        );

      case 'radio':
        return (
          <FormControl
            component="fieldset"
            error={error}
            disabled={disabled}
            required={required}
            sx={sx}
          >
            <FormLabel component="legend">{label}</FormLabel>
            <RadioGroup
              value={isUsingRegister ? undefined : (value || '')}
              onChange={isUsingRegister ? undefined : onChange}
              name={name}
              {...otherProps}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        );

      case 'date':
        return (
          <TextField
            label={label}
            value={isUsingRegister ? undefined : (value || '')}
            onChange={isUsingRegister ? undefined : onChange}
            name={name}
            type="date"
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            sx={sx}
            inputProps={{
              ...inputProps,
              ...otherProps,
            }}
          />
        );

      case 'time':
        return (
          <TextField
            label={label}
            value={isUsingRegister ? undefined : (value || '')}
            onChange={isUsingRegister ? undefined : onChange}
            name={name}
            type="time"
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            sx={sx}
            inputProps={{
              ...inputProps,
              ...otherProps,
            }}
          />
        );

      case 'textarea':
        return (
          <TextField
            label={label}
            value={isUsingRegister ? undefined : (value || '')}
            onChange={isUsingRegister ? undefined : onChange}
            name={name}
            multiline
            rows={rows}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            sx={sx}
            inputProps={inputProps}
            {...otherProps}
          />
        );

      case 'display':
        return (
          <Box sx={sx}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {label}
            </Typography>
            <Typography variant="body1">
              {value || placeholder || 'No value'}
            </Typography>
          </Box>
        );

      default:
        return (
          <TextField
            label={label}
            value={isUsingRegister ? undefined : (value || '')}
            onChange={isUsingRegister ? undefined : onChange}
            name={name}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            fullWidth={fullWidth}
            size={size}
            variant={variant}
            sx={sx}
            inputProps={inputProps}
            {...otherProps}
          />
        );
    }
  };

  return renderField();
};

export default ReusableField;
